/**
 * The one read of «my access» — `GET /api/v1/access/me` (E1.5, #1336, #1645).
 *
 * Every surface that decides which screens a subject is offered reads the
 * answer through this hook (via PermissionContext in the portal, via
 * `useEmbeddedAccess` in the Bitrix24 iframe) and hands it to
 * `decideScreen` / `dashboardFor` (`lib/route-permissions.ts`).
 *
 * Keyed by the selected organization AND the signed-in user: a grant is per
 * organization and per person, so neither an organization switch nor another
 * user signing in on the same organization may see the previous answer.
 *
 * Also the owner of the access revision in the UI (spec PR 4b,
 * `lib/access-revision.ts`), which no query key carries:
 *
 * - the store is bound to the (organization, subject) pair this hook reads
 *   for; a different pair resets it and sweeps what the previous pair fetched
 *   (A16);
 * - a growth taught by any other answer cancels a read on its way and asks
 *   again; an answer behind the known revision is asked once more;
 * - each new answer is compared with the previous one by what the viewer may
 *   see (`accessFingerprint`). Changed — every access-dependent query is reset
 *   and open panels close (`useAccessChange`). Unchanged but the revision grew
 *   (someone else's change in the organization) — data refreshes in the
 *   background and nothing closes;
 * - the fingerprint includes the viewer's role, and the role lives in another
 *   cache (`user-data`, the embedded session) that no sweep touches: a role
 *   change leaves `/access/me` byte-for-byte the same. So on a growth the role
 *   source is re-read TOGETHER with `/access/me` (`refreshViewer`), and nothing
 *   is decided until both settled and the render holds both — whichever
 *   arrives first must not decide «unchanged» (Codex P1, #1745);
 * - a growth settles only on an answer actually obtained: a failed re-read of
 *   `/access/me` is asked again (`ACCESS_REREAD_BACKOFF_MS`), and when it still
 *   fails the viewer's access is UNKNOWN — handled as a change (reset, panels
 *   close), never as «unchanged» by the previous answer (Codex P1, #1745);
 * - the same holds for the role source: a role re-read that could not be
 *   obtained is UNKNOWN, not «the role I render» — asked again the same bounded
 *   way, then failed closed (a role reread that failed used to accept the
 *   still-rendered old viewer and call a revoked role «unchanged»; Codex P1,
 *   #1745).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useQuery,
  useQueryClient,
  type Query,
  type QueryClient,
} from '@tanstack/react-query';
import type { AccessMeResponse } from '@aica/api-types/schemas';
import type { AccessState } from '@/lib/route-permissions';
import { logError } from '@/lib/logger';
import { retryTransientOnly } from '@/lib/query-retry';
import {
  bindAccessPair,
  isIdentityQuery,
  noteAccessChange,
  readKnownRevision,
  setAccessRevisionGrowthHandler,
  type AccessPair,
} from '@/lib/access-revision';
import { clearAudioBlobCache } from '@/lib/records/audio-blob-cache';

export const accessMeQueryKey = (orgId: string | null, subjectId: string | null) =>
  ['access-me', orgId, subjectId] as const;

/**
 * Is `/access/me` being asked again — or about to be, on this very focus or
 * reconnect? A background refetch keeps the PREVIOUS answer in hand, so a
 * reader that decides by it meanwhile may decide by an access that was just
 * revoked. Read live from the query cache, not from a render: the focus event
 * that starts the refetch reaches the readers in the same tick, before any
 * render could report it, and a stale answer is the one that WILL refetch.
 */
export function isAccessRefetching(
  queryClient: QueryClient,
  orgId: string | null
): boolean {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: ['access-me', orgId] })
    .some((query) => query.state.fetchStatus === 'fetching' || query.isStale());
}

/**
 * The pair the session left is gone for good when its organization or its
 * (signed-in) subject changed. A subject appearing for the first time on a
 * pair the session has not been signed into yet is no departure.
 */
function leftPair(previous: AccessPair, next: AccessPair): boolean {
  return (
    (previous.orgId !== null && previous.orgId !== next.orgId) ||
    (previous.subjectId !== null && previous.subjectId !== next.subjectId)
  );
}

/**
 * Cancel and remove what the pair that was left fetched or holds. Run in a
 * layout effect — after the render that switched pairs, before any passive
 * effect starts the new pair's fetches — so every query that is fetching or
 * carries data at that moment is the previous pair's. (The organization
 * switch itself sweeps the same set before it changes the organization; this
 * covers a subject change, which nothing else does.)
 */
function sweepPreviousPair(queryClient: QueryClient): void {
  // Collected BEFORE cancelling: a cancelled fetch reverts to «idle, no data»,
  // which no longer says it belonged to anyone.
  const previous = queryClient.getQueryCache().findAll({
    predicate: (query) =>
      !isIdentityQuery(query.queryKey) &&
      (query.state.fetchStatus === 'fetching' ||
        query.state.dataUpdatedAt > 0 ||
        query.state.errorUpdatedAt > 0),
  });
  const queryHashes = new Set(previous.map((query) => query.queryHash));
  const isPrevious = (query: { queryHash: string }) => queryHashes.has(query.queryHash);
  void queryClient.cancelQueries({ predicate: isPrevious });
  queryClient.removeQueries({ predicate: isPrevious });
  clearAudioBlobCache();
}

/**
 * What depends on the viewer's access: everything but the identity roster and
 * «my access» itself — the answer that announced a change is the one the
 * screens are about to be decided by.
 */
const isAccessDependent = (query: Query) =>
  !isIdentityQuery(query.queryKey) && query.queryKey[0] !== 'access-me';

/**
 * Waits before the repeats of a failed `/access/me` re-read on a growth — one
 * repeat per entry. Bounded: the answer that would have said «revoked» is not
 * waited for forever, the viewer is failed closed instead.
 */
export const ACCESS_REREAD_BACKOFF_MS: readonly number[] = [200, 600];

/** The viewer as the role decides it — part of what «my access» means. */
export interface AccessViewer {
  globalRole: string | null;
  isOwner: boolean;
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, item]) => [key, canonical(item)])
    );
  }
  return value;
}

/**
 * What the viewer may see, as one comparable string: the policy without its
 * revision (which grows for anyone's change in the organization), plus the
 * role and the owner flag.
 */
export function accessFingerprint(
  access: AccessMeResponse,
  viewer: AccessViewer
): string {
  const { revision: _revision, ...policy } = access;
  return JSON.stringify(
    canonical({ policy, globalRole: viewer.globalRole, isOwner: viewer.isOwner })
  );
}

export interface AccessMeSource {
  /** The validated read (`apiClient.access.getMe`). */
  getMe: () => Promise<AccessMeResponse>;
  orgId: string | null;
  /**
   * Who is asking — the Clerk user in the portal. `null` in the Bitrix24
   * iframe: one iframe load is one portal user's session, and its query cache
   * dies with the frame.
   */
  subjectId: string | null;
  /** False while the session the read needs is not ready yet. */
  enabled: boolean;
  /**
   * The role and owner flag the access is compared with; `null` while they
   * are not known — nothing is compared until they are (a role arriving after
   * the first answer is not a change).
   */
  viewer: AccessViewer | null;
  /**
   * Re-read what `viewer` is derived from (the portal's `user-data`, the
   * embedded session) and resolve with the viewer it now says — `null` when
   * it could not be read (asked again a bounded number of times, then the
   * viewer is failed closed). Called on every growth of the revision, because
   * a role change grows it without changing the `/access/me` answer.
   */
  refreshViewer?: () => Promise<AccessViewer | null>;
}

/**
 * A growth of the revision is being settled: `reading` until the role source
 * and `/access/me` both answered, then `settled` until the render holds both
 * answers — only then is the fingerprint compared.
 */
type Settling = { phase: 'reading' } | { phase: 'settled'; viewer: AccessViewer | null };

/**
 * Asking `/access/me` again on a growth. `run` resolves `true` once a current
 * answer was obtained, `false` when every attempt failed; `isCurrent` says the
 * growth being settled is still the latest (a superseded one has no verdict).
 */
interface AccessReread {
  queryKey: readonly unknown[];
  run: (isCurrent: () => boolean) => Promise<boolean>;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Cancel the read on its way and ask `/access/me` afresh — a plain
 * invalidation JOINS a cold read in flight instead of asking again (Codex P2,
 * #1745) — until an answer received after this call began is in the cache. A failure is
 * asked again after each wait of `ACCESS_REREAD_BACKOFF_MS`; `false` when they
 * all failed. A read cancelled by a newer growth is that growth's business.
 */
async function rereadAccessMe(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  isCurrent: () => boolean
): Promise<boolean> {
  // The count of answers the query ever received: it only grows, so «newer
  // than this call» does not depend on clock resolution.
  const answersBefore = queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  for (let attempt = 0; ; attempt += 1) {
    await queryClient.cancelQueries({ queryKey });
    if (!isCurrent()) return true;
    try {
      await queryClient.invalidateQueries({ queryKey }, { throwOnError: true });
    } catch {
      // The verdict is the query's state below, not the rejection.
    }
    if (!isCurrent()) return true;
    const state = queryClient.getQueryState(queryKey);
    if (state?.status === 'success' && state.dataUpdateCount > answersBefore) return true;
    const wait = ACCESS_REREAD_BACKOFF_MS[attempt];
    if (wait === undefined) return false;
    await sleep(wait);
    if (!isCurrent()) return true;
  }
}

/**
 * Ask the role source again until it answers: `null` when every attempt failed
 * (or a newer growth took over — the caller's `isCurrent` has the last word).
 * The same bounded waits as the `/access/me` re-read.
 */
async function rereadViewer(
  read: () => Promise<AccessViewer | null>,
  isCurrent: () => boolean
): Promise<AccessViewer | null> {
  for (let attempt = 0; ; attempt += 1) {
    const fresh = await read().catch(() => null);
    if (fresh !== null || !isCurrent()) return fresh;
    const wait = ACCESS_REREAD_BACKOFF_MS[attempt];
    if (wait === undefined) return null;
    await sleep(wait);
    if (!isCurrent()) return null;
  }
}

/**
 * The two answers that carry no data. Module constants, so a consumer sees the
 * same object for as long as the state lasts.
 */
const ACCESS_LOADING: AccessState = Object.freeze({ status: 'loading' });
const ACCESS_FAILED: AccessState = Object.freeze({ status: 'failed' });

/** The answer the screens were last decided by, and for which pair. */
interface SeenAccess {
  orgId: string | null;
  subjectId: string | null;
  fingerprint: string;
  revision: number;
}

export function useAccessMe({
  getMe,
  orgId,
  subjectId,
  enabled,
  viewer,
  refreshViewer,
}: AccessMeSource): AccessState {
  const queryClient = useQueryClient();
  const settling = useRef<Settling | null>(null);
  // Which growth is the latest: an older one finishing late settles nothing.
  const settlingSeq = useRef(0);
  // The revision the role source was last re-read for (this pair).
  const viewerCheckedRevision = useRef(-1);
  // Bumped when a growth settled: the comparison below runs again.
  const [settleTick, setSettleTick] = useState(0);
  const refreshViewerRef = useRef(refreshViewer);
  refreshViewerRef.current = refreshViewer;
  const seen = useRef<SeenAccess | null>(null);
  // Bumped after an access change reset the queries: their refetch waits for
  // the render that closed the panels (below).
  const [resetCount, setResetCount] = useState(0);
  // Every answer read under the old access is dropped, in-flight ones
  // cancelled — reset rather than removed, so a mounted screen learns it lost
  // its data (its key does not change, nothing else would re-render it).
  const dropAccessDependentReads = useCallback(() => {
    for (const stale of queryClient
      .getQueryCache()
      .findAll({ predicate: isAccessDependent })) {
      stale.reset();
    }
    clearAudioBlobCache();
  }, [queryClient]);
  /**
   * The viewer's access could not be re-read after a growth: it is unknown, and
   * unknown is not «unchanged». Handled as a change — what the old access read
   * is reset, panels close — and the `/access/me` answer in hand is dropped, so
   * the shell shows the failed read and its retry instead of deciding by it.
   * Whatever answer comes next is the new baseline.
   */
  const failClosed = useCallback(
    (queryKey: readonly unknown[]) => {
      settling.current = null;
      seen.current = null;
      dropAccessDependentReads();
      noteAccessChange();
      setResetCount((count) => count + 1);
      void queryClient.resetQueries({ queryKey });
    },
    [queryClient, dropAccessDependentReads]
  );
  /**
   * Re-read the role source, and — when a growth taught by another answer
   * calls for it — `/access/me`; hold the comparison until both settled. An
   * answer that could not be obtained — `/access/me`, or the role when there
   * is a role source to ask — fails the viewer closed.
   */
  const settle = useCallback(
    (revision: number, queryKey: readonly unknown[], reread: AccessReread | null) => {
      const mine = ++settlingSeq.current;
      const isCurrent = () => mine === settlingSeq.current;
      settling.current = { phase: 'reading' };
      viewerCheckedRevision.current = revision;
      const askViewer = refreshViewerRef.current;
      const viewerRead = askViewer
        ? rereadViewer(askViewer, isCurrent)
        : Promise.resolve<AccessViewer | null>(null);
      void Promise.all([
        viewerRead,
        reread ? reread.run(isCurrent).catch(() => false) : true,
      ]).then(([fresh, obtained]) => {
        if (!isCurrent()) return;
        if (!obtained || (askViewer && fresh === null)) {
          failClosed(queryKey);
          return;
        }
        settling.current = { phase: 'settled', viewer: fresh };
        setSettleTick((tick) => tick + 1);
      });
    },
    [failClosed]
  );
  // Bound before any passive effect (every fetch of the new pair starts in
  // one): the store is reset and the previous pair's queries are gone.
  useLayoutEffect(() => {
    const previous = bindAccessPair(orgId, subjectId);
    if (previous && leftPair(previous, { orgId, subjectId })) {
      sweepPreviousPair(queryClient);
      // What the left pair showed is not this one's: its open panels —
      // popovers over its numbers, a report panel, a record card — close
      // as on a change of access (#1341, «org switch closes the old details»).
      noteAccessChange();
      // What was being settled belonged to the pair that was left.
      settlingSeq.current += 1;
      settling.current = null;
      viewerCheckedRevision.current = -1;
    }
  }, [orgId, subjectId, queryClient]);
  // Another answer taught the UI a newer revision: the read on its way is
  // cancelled — a plain invalidation JOINS a cold read in flight instead of
  // asking again (Codex P2, #1745) — and «my access» is asked afresh, with the
  // role source beside it. A growth `/access/me` taught itself never gets
  // here (`lib/access-revision.ts`); the comparison below covers it.
  useEffect(
    () =>
      setAccessRevisionGrowthHandler((growthOrgId, growthSubjectId) => {
        const queryKey = accessMeQueryKey(growthOrgId, growthSubjectId);
        settle(readKnownRevision(growthOrgId, growthSubjectId) ?? 0, queryKey, {
          queryKey,
          run: (isCurrent) => rereadAccessMe(queryClient, queryKey, isCurrent),
        });
      }),
    [queryClient, settle]
  );

  // Unmounted: whatever growth is still being settled has no verdict. Its
  // reread may fail after the next session mounted on the same QueryClient —
  // `failClosed` would then reset THAT session's queries and clear its audio.
  // Bumping the sequence makes every pending step of `settle` a no-op.
  useEffect(
    () => () => {
      settlingSeq.current += 1;
      settling.current = null;
    },
    []
  );

  const queryEnabled = enabled && !!orgId;
  const query = useQuery<AccessMeResponse, Error>({
    queryKey: accessMeQueryKey(orgId, subjectId),
    queryFn: async ({ signal }) => {
      const answer = await getMe();
      // A read cancelled for a fresh one (above) is nobody's answer any more.
      if (signal.aborted) return answer;
      // Behind what another answer already taught (it left before the change
      // reached the server's snapshot): asked once more — once, not in a loop.
      const known = readKnownRevision(orgId, subjectId);
      return known !== null && answer.revision < known ? getMe() : answer;
    },
    enabled: queryEnabled,
    // A 503 `access_policy_unavailable` or a dropped connection is asked once
    // more; a 4xx answers the same however often it is asked.
    retry: retryTransientOnly,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // A grant assigned or revoked while the tab was in the background.
    refetchOnWindowFocus: true,
  });

  const { data, isError, error, errorUpdatedAt, refetch } = query;
  const viewerKnown = viewer !== null;
  const viewerRole = viewer?.globalRole ?? null;
  const viewerIsOwner = viewer?.isOwner ?? false;
  const fingerprint = useMemo(
    () =>
      data && viewerKnown
        ? accessFingerprint(data, { globalRole: viewerRole, isOwner: viewerIsOwner })
        : null,
    [data, viewerKnown, viewerRole, viewerIsOwner]
  );
  // A layout effect: the reset and the change count land before any passive
  // effect of this commit could start a fetch under the old access.
  useLayoutEffect(() => {
    if (!data || fingerprint === null) return;
    // A growth is being settled: not decided by whichever answer came first.
    const settlingNow = settling.current;
    if (settlingNow) {
      if (settlingNow.phase === 'reading') return;
      // Both reads answered; wait for the render that holds both — the cache
      // is the truth for «my access», the role source's answer for the viewer.
      const cached = queryClient.getQueryData(accessMeQueryKey(orgId, subjectId));
      const accessArrived = cached === undefined || cached === data;
      // `null`: there is no role source to wait for (an unobtainable one never
      // gets here — it failed closed).
      const fresh = settlingNow.viewer;
      const viewerArrived =
        fresh === null ||
        (fresh.globalRole === viewerRole && fresh.isOwner === viewerIsOwner);
      if (!accessArrived || !viewerArrived) return;
      settling.current = null;
    }
    const previous = seen.current;
    const sameBaseline =
      previous !== null && previous.orgId === orgId && previous.subjectId === subjectId;
    if (
      sameBaseline &&
      data.revision > previous.revision &&
      refreshViewerRef.current !== undefined &&
      viewerCheckedRevision.current < data.revision
    ) {
      // «My access» alone taught this growth. Whether or not the policy looks
      // the same, the role may not be: a policy and a role changed together
      // would otherwise be decided on the role still rendered, because the
      // role source (`user-data`) does not refetch on focus. Ask it before
      // deciding anything. `seen` is left as it was, so the comparison runs
      // again, growth and all, once the role source answered.
      if (previous.fingerprint !== fingerprint) {
        // The policy itself changed: what was read under the old one is dropped
        // now, not after a role read that may be slow — the decision (panels,
        // the change count) still waits for the role.
        dropAccessDependentReads();
      }
      settle(data.revision, accessMeQueryKey(orgId, subjectId), null);
      return;
    }
    seen.current = { orgId, subjectId, fingerprint, revision: data.revision };
    // The first answer of a pair is the baseline, not a change.
    if (!sameBaseline) return;
    if (previous.fingerprint !== fingerprint) {
      // What the viewer may see changed. Every answer read under the old
      // access is dropped, in-flight ones cancelled — reset rather than
      // removed, so a mounted screen learns it lost its data (its key does not
      // change, nothing else would re-render it) — and the panels close.
      // Nothing is refetched yet: a record card still mounted in this commit
      // would ask for its record under the new access before it closes.
      dropAccessDependentReads();
      noteAccessChange();
      setResetCount((count) => count + 1);
      return;
    }
    if (data.revision > previous.revision) {
      // Someone else's change in the organization: the viewer's access is the
      // same, the data under it may not be. Refreshed in the background; what
      // is on screen stays until the new answer replaces it.
      void queryClient.invalidateQueries({ predicate: isAccessDependent });
    }
  }, [
    data,
    fingerprint,
    viewerRole,
    viewerIsOwner,
    settleTick,
    orgId,
    subjectId,
    queryClient,
    settle,
    dropAccessDependentReads,
  ]);
  // After the render that closed the panels: what is still mounted asks again.
  useEffect(() => {
    if (resetCount === 0) return;
    void queryClient.refetchQueries({ predicate: isAccessDependent, type: 'active' });
  }, [resetCount, queryClient]);

  // A read that failed after its retries is a fault worth seeing (#1677): the
  // role decides meanwhile, and nobody would know why a grant holder's screens
  // look like its role's. Logged once per failure, not per render.
  useEffect(() => {
    if (!error) return;
    logError(
      'access.me.read_failed',
      { orgId },
      error instanceof Error ? error : undefined
    );
  }, [error, errorUpdatedAt, orgId]);
  // Asked and failed with no answer in hand: unknown, not «no grant» — the
  // shell offers to ask again (`AccessState`). Keyed by `refetch`, which the
  // query keeps for its life: the same object across renders and repeated
  // failures, so a consumer keyed by the state is not re-rendered by them.
  // A retry joins a read already on its way instead of cancelling it.
  const unknown = useMemo(
    (): AccessState => ({
      status: 'failed',
      retry: async () => {
        await refetch({ cancelRefetch: false });
      },
    }),
    [refetch]
  );
  // Keyed by the answer alone: a failed background refetch keeps `data` and
  // must not hand every permission consumer a new object with the same answer
  // (it re-rendered all of them). An answer in hand also survives that
  // failure: a background blip must not turn a grant holder's screens back
  // into the role's.
  const resolved = useMemo(
    (): AccessState | null => (data ? { status: 'resolved', access: data } : null),
    [data]
  );
  if (resolved) return resolved;
  // Not asked — no organization, or no session to ask with — is no answer,
  // not one on its way: a disabled query would report `loading` for good and
  // keep a screen waiting on it. The role decides, as on a failed read.
  if (!queryEnabled) return ACCESS_FAILED;
  if (isError) return unknown;
  return ACCESS_LOADING;
}
